import {
  useAccountUsers,
  useCreateUser,
  useDeleteUser,
  useUpdateUser,
} from '@my/directory-api/hooks';
import type { AppRoleSummary, AppRoleValue, User } from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  FormSection,
  Loading,
  SelectInput,
  TextInput,
  showToast,
} from '@my/ui';
import { Check, Edit, Plus, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface AdministratorsSectionProps {
  /** Current organisation id; users without a matching app-role are filtered out. */
  organisationId: string | null;
}

/**
 * Bottom-row card on the Organisation page. Lists users with **Owner** or
 * **Admin** platform-role on the current organisation (TMS) and lets the
 * caller add / promote / demote / remove inside that subset.
 *
 * Lower-tier roles (Manager / User / Viewer) and functional / derived
 * roles are not shown here — they live on the dedicated Users & Access
 * screen (link in the header). This card stays focused on "who runs
 * this org" so the Organisation page doesn't sprawl into full user
 * management.
 *
 * BE rules enforced at the handler layer (no FE pre-checks):
 *  - Demoting / removing the only Owner of an org-app is rejected with
 *    `ErrorKind.Validation`. Errors surface in toasts.
 */
export function AdministratorsSection({ organisationId }: AdministratorsSectionProps) {
  const { t } = useTranslation('organisation');
  const usersQuery = useAccountUsers();
  const createMutation = useCreateUser();
  const updateMutation = useUpdateUser();
  const deleteMutation = useDeleteUser();

  // `null` = no editor open; `'new'` = drafting a fresh admin invite;
  // a guid = editing the row with that id. One editor open at a time.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<AdminForm>(emptyForm());
  const [submitted, setSubmitted] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AdminRow | null>(null);

  const isBusy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Project the User[] list down to "Owner / Admin on THIS org", carrying
  // the underlying user record so the editor can pre-fill firstName /
  // lastName accurately. Owner sorted first, then alphabetical.
  const rows = useMemo<AdminRow[]>(() => {
    if (!organisationId || !usersQuery.data) return [];
    return usersQuery.data
      .map((u) => deriveAdminRow(u, organisationId))
      .filter((r): r is AdminRow => r !== null)
      .sort((a, b) => {
        if (a.role !== b.role) return a.role === 'Owner' ? -1 : 1;
        return a.fullName.localeCompare(b.fullName);
      });
  }, [usersQuery.data, organisationId]);

  // Owner count drives the FE last-Owner guard — UX preflight for the
  // BE rule. Counts via the rows projection (which already filters to
  // active TMS grants on this org) so anything the user can SEE here
  // contributes; soft-deleted memberships don't.
  const ownerCount = useMemo(
    () => rows.filter((r) => r.role === 'Owner').length,
    [rows],
  );

  const startEdit = (row: AdminRow) => {
    setEditingId(row.id);
    setDraft(toForm(row));
    setSubmitted(false);
  };

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyForm());
    setSubmitted(false);
  };

  const handleSave = async () => {
    if (!organisationId || editingId === null) return;
    setSubmitted(true);
    if (!isFormValid(draft, editingId === 'new')) return;
    // FE last-Owner guard. Mirrors the BE rule on UpdateUser.
    const editingRow =
      typeof editingId === 'string' && editingId !== 'new'
        ? rows.find((r) => r.id === editingId)
        : undefined;
    if (
      editingRow &&
      editingRow.role === 'Owner' &&
      draft.role !== 'Owner' &&
      ownerCount === 1
    ) {
      return;
    }

    try {
      if (editingId === 'new') {
        await createMutation.mutateAsync({
          organisationId,
          email: draft.email.trim().toLowerCase(),
          firstName: draft.firstName.trim() || null,
          lastName: draft.lastName.trim() || null,
          mobileNumber: draft.mobileNumber.trim() || null,
          role: draft.role,
          // No functional roles touched from this screen — that lives on
          // the Users & Access page. Send `null` so the BE leaves them
          // alone (a no-op for fresh creates; preserves any existing
          // assignments on the resurrection-from-soft-delete path).
          functionalRoles: null,
        });
        showToast({ type: 'success', message: t('users.added') });
      } else {
        await updateMutation.mutateAsync({
          userId: editingId,
          organisationId,
          firstName: draft.firstName.trim() || null,
          lastName: draft.lastName.trim() || null,
          // Empty string clears the value; trimmed input otherwise.
          mobileNumber: draft.mobileNumber.trim(),
          role: draft.role,
          functionalRoles: null,
        });
        showToast({ type: 'success', message: t('users.updated') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('users.saveFailed'),
      });
    }
  };

  const requestDelete = (row: AdminRow) => {
    if (!organisationId) return;
    if (row.role === 'Owner' && ownerCount === 1) return;
    setPendingDelete(row);
  };

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row || !organisationId) return;
    try {
      await deleteMutation.mutateAsync({ id: row.id, organisationId });
      showToast({ type: 'success', message: t('users.removed') });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('users.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // Single "+ Add" affordance, matching the convention every other card
  // on the Organisation page uses (Bank Accounts, Currencies, Tax
  // Schemas). Hides while an editor is open — single-editor rule.
  const headerAction =
    usersQuery.isSuccess && editingId === null ? (
      <button
        type="button"
        aria-label={t('users.addAria')}
        onClick={startNew}
        disabled={!organisationId}
        className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('users.add')}
      </button>
    ) : null;

  return (
    <FormSection title={t('administrators.title')} columns={1} actions={headerAction}>
      {usersQuery.isPending && <Loading label={t('administrators.loading')} />}
      {usersQuery.isError && (
        <ErrorBanner
          title={t('administrators.loadFailed')}
          message={usersQuery.error.message}
        />
      )}

      {usersQuery.isSuccess && (
        <ul className="space-y-2">
          {/* Fresh-invite editor at the top, mirroring the BankAccounts /
              Currencies / TaxSchemas pattern. Edits to existing rows
              render in-place further down. */}
          {editingId === 'new' && (
            <li>
              <AdminEditor
                draft={draft}
                onChange={setDraft}
                submitted={submitted}
                isNew
                onSave={handleSave}
                onCancel={cancel}
                isBusy={isBusy}
              />
            </li>
          )}

          {rows.length === 0 && editingId !== 'new' && (
            <li className="text-sm text-slate-500">{t('administrators.empty')}</li>
          )}

          {rows.map((row) => {
            const isLastOwner = row.role === 'Owner' && ownerCount === 1;
            return editingId === row.id ? (
              <li key={row.id}>
                <AdminEditor
                  draft={draft}
                  onChange={setDraft}
                  submitted={submitted}
                  isNew={false}
                  originalRole={row.role}
                  isLastOwner={isLastOwner}
                  onSave={handleSave}
                  onCancel={cancel}
                  onDelete={isLastOwner ? undefined : () => requestDelete(row)}
                  isBusy={isBusy}
                />
              </li>
            ) : (
              <li
                key={row.id}
                className="flex items-center gap-3 rounded-sm border border-slate-200 px-3 py-2"
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700"
                  aria-hidden
                >
                  {row.initials}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {row.fullName}
                  </p>
                  <p className="truncate text-xs text-slate-500">{row.email}</p>
                </div>
                <RoleBadge role={row.role} />
                <button
                  type="button"
                  aria-label={t('users.ariaEdit', { name: row.fullName })}
                  onClick={() => startEdit(row)}
                  className="flex h-7 w-7 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50"
                >
                  <Edit className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('users.deleteDialog.title')}
        message={
          pendingDelete
            ? t('users.deleteDialog.message', { name: pendingDelete.fullName })
            : null
        }
        confirmLabel={t('users.deleteDialog.confirm')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </FormSection>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Row projection
// ────────────────────────────────────────────────────────────────────────────

interface AdminRow {
  id: string;
  fullName: string;
  email: string;
  initials: string;
  role: 'Owner' | 'Admin';
  /** Used by `toForm` to pre-fill the editor without splitting `fullName`. */
  firstName: string;
  lastName: string;
  mobileNumber: string;
}

/**
 * Project a `User` to the row shape this section renders. Returns `null`
 * unless the user has `Owner` or `Admin` for the given organisation in
 * TMS — lower roles surface only on Users & Access.
 */
function deriveAdminRow(user: User, organisationId: string): AdminRow | null {
  let topGrant: AppRoleSummary | null = null;
  let topRole: 'Owner' | 'Admin' | null = null;

  outer: for (const account of user.accounts) {
    if (account.status === 'Suspended' || account.status === 'Left') continue;
    for (const r of account.appRoles) {
      if (r.app !== 'Tms') continue;
      if (r.organisationId !== organisationId) continue;
      if (r.role === 'Owner') {
        topRole = 'Owner';
        topGrant = r;
        break outer;
      }
      if (r.role === 'Admin' && topRole === null) {
        topRole = 'Admin';
        topGrant = r;
      }
    }
  }
  if (!topRole || !topGrant) return null;
  return {
    id: user.id,
    fullName: user.fullName || user.email,
    email: user.email,
    initials: user.initials || initialsFallback(user.email),
    role: topRole,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    mobileNumber: user.mobileNumber ?? '',
  };
}

function initialsFallback(email: string): string {
  return email.slice(0, 2).toUpperCase();
}

// ────────────────────────────────────────────────────────────────────────────
// Editor form
// ────────────────────────────────────────────────────────────────────────────

interface AdminForm {
  email: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  /** Constrained to Owner / Admin in this section's UI. */
  role: 'Owner' | 'Admin';
}

function emptyForm(): AdminForm {
  return {
    email: '',
    firstName: '',
    lastName: '',
    mobileNumber: '',
    // Default to Admin — Owner promotion is intentional, not the default
    // when inviting "another admin to help manage the org".
    role: 'Admin',
  };
}

function toForm(row: AdminRow): AdminForm {
  return {
    email: row.email,
    firstName: row.firstName,
    lastName: row.lastName,
    mobileNumber: row.mobileNumber,
    role: row.role,
  };
}

function isFormValid(form: AdminForm, isNew: boolean): boolean {
  if (isNew) {
    if (!form.email.trim()) return false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return false;
  }
  if (form.mobileNumber.length > 32) return false;
  return true;
}

interface AdminEditorProps {
  draft: AdminForm;
  onChange: (next: AdminForm) => void;
  submitted: boolean;
  isNew: boolean;
  /** The role this user had before the edit started, if editing. */
  originalRole?: 'Owner' | 'Admin';
  /** True when the user is the only Owner on this org-app. */
  isLastOwner?: boolean;
  onSave: () => void;
  onCancel: () => void;
  /** Omit to hide the trash button (used to lock delete on the last Owner). */
  onDelete?: () => void;
  isBusy: boolean;
}

function AdminEditor({
  draft,
  onChange,
  submitted,
  isNew,
  originalRole,
  isLastOwner = false,
  onSave,
  onCancel,
  onDelete,
  isBusy,
}: AdminEditorProps) {
  const { t } = useTranslation('organisation');
  const set = <K extends keyof AdminForm>(key: K, value: AdminForm[K]) =>
    onChange({ ...draft, [key]: value });

  // Only Owner / Admin offered here. Lower roles live on Users & Access.
  const roleOptions: { value: AppRoleValue; label: string }[] = [
    { value: 'Owner', label: t('roles.platformRoles.Owner', { defaultValue: 'Owner' }) },
    { value: 'Admin', label: t('roles.platformRoles.Admin', { defaultValue: 'Admin' }) },
  ];

  // Demoting the only Owner of this org-app would leave nobody able to
  // manage it. Surface inline + lock Save. Mirrors the BE rule.
  const wouldDemoteLastOwner =
    !isNew && isLastOwner && originalRole === 'Owner' && draft.role !== 'Owner';

  const errs = {
    email:
      isNew &&
      (draft.email.trim().length === 0 ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim())),
  };

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="space-y-3">
        <TextInput
          label={t('users.fields.email')}
          name="email"
          type="email"
          autoComplete="off"
          value={draft.email}
          maxLength={256}
          // BE doesn't expose an email-change endpoint — locking the
          // field on edit avoids the "saved but didn't change" trap.
          disabled={!isNew}
          onChange={(e) => set('email', e.target.value)}
          error={submitted && errs.email}
        />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <TextInput
            label={t('users.fields.firstName')}
            name="firstName"
            autoComplete="off"
            value={draft.firstName}
            maxLength={128}
            onChange={(e) => set('firstName', e.target.value)}
          />
          <TextInput
            label={t('users.fields.lastName')}
            name="lastName"
            autoComplete="off"
            value={draft.lastName}
            maxLength={128}
            onChange={(e) => set('lastName', e.target.value)}
          />
        </div>
        <TextInput
          label={t('users.fields.mobileNumber')}
          name="mobileNumber"
          type="tel"
          autoComplete="off"
          value={draft.mobileNumber}
          maxLength={32}
          placeholder={t('users.fields.mobileNumberPlaceholder')}
          onChange={(e) => set('mobileNumber', e.target.value)}
        />
        <div>
          <SelectInput
            label={t('users.fields.platformRole')}
            name="role"
            value={draft.role}
            onChange={(e) => set('role', e.target.value as 'Owner' | 'Admin')}
            options={roleOptions}
          />
          {wouldDemoteLastOwner && (
            <p className="mt-1 text-xs text-red-600">
              {t('users.validation.cannotDemoteLastOwner')}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-1">
        <div>
          {onDelete ? (
            <button
              type="button"
              aria-label={t('users.ariaDelete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : isLastOwner ? (
            <button
              type="button"
              aria-label={t('users.ariaDelete')}
              disabled
              title={t('users.validation.cannotRemoveLastOwner')}
              className="flex h-7 w-7 cursor-not-allowed items-center justify-center rounded-sm text-red-600 opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('users.ariaCancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('users.ariaSave')}
            onClick={onSave}
            disabled={isBusy || wouldDemoteLastOwner}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Badge
// ────────────────────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: AppRoleValue }) {
  const { t } = useTranslation('organisation');
  // Owner blue, Admin purple — keeps visual hierarchy consistent with
  // the rest of the suite (primary blue for top-tier).
  const tone =
    role === 'Owner'
      ? 'bg-blue-50 text-blue-700 ring-blue-200'
      : 'bg-purple-50 text-purple-700 ring-purple-200';
  return (
    <span
      className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${tone}`}
    >
      {t(`roles.platformRoles.${role}`, { defaultValue: role })}
    </span>
  );
}
